import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Users, Mail, Trash2, CheckCircle, XCircle, ArrowLeft, Eye, 
  Calendar, DollarSign, Activity, Ban, Shield, Clock, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ui/confirm-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCRM = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // User detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailModal, setUserDetailModal] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  
  // Edit subscription modal
  const [editSubModal, setEditSubModal] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  
  // Email modal
  const [emailModal, setEmailModal] = useState({ open: false, user: null });
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Confirmation dialogs
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, userId: null });
  const [suspendUserDialog, setSuspendUserDialog] = useState({ open: false, userId: null, currentStatus: null });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/admin/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, filterPlan, filterStatus]);

  const fetchUsers = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Plan filter
    if (filterPlan !== 'all') {
      filtered = filtered.filter(u => u.subscription_plan === filterPlan);
    }
    
    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(u => u.account_status === filterStatus || (!u.account_status && filterStatus === 'active'));
    }
    
    setFilteredUsers(filtered);
  };

  const handleViewUserDetails = async (selectedUser) => {
    const token = localStorage.getItem('session_token');
    setSelectedUser(selectedUser);
    setUserDetailModal(true);
    
    try {
      // Fetch user statistics, billing history, and subscription history
      const [statsRes, billingRes, subHistoryRes] = await Promise.all([
        axios.get(`${API}/admin/users/${selectedUser.id}/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users/${selectedUser.id}/billing-history`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users/${selectedUser.id}/subscription-history`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setUserStats(statsRes.data);
      setBillingHistory(billingRes.data);
      setSubscriptionHistory(subHistoryRes.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to load user details');
    }
  };

  const handleUpdateSubscription = async (userId, plan) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(
        `${API}/admin/users/${userId}/subscription?plan=${plan}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleEditSubscriptionDates = () => {
    if (selectedUser) {
      setSubscriptionEndDate(selectedUser.subscription_end_date ? 
        new Date(selectedUser.subscription_end_date).toISOString().split('T')[0] : '');
      setSubscriptionStatus(selectedUser.subscription_status || 'active');
      setEditSubModal(true);
    }
  };

  const handleSaveSubscriptionDates = async () => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}/subscription-dates`,
        {
          end_date: subscriptionEndDate,
          status: subscriptionStatus
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription dates updated successfully');
      setEditSubModal(false);
      fetchUsers();
      // Refresh user details if modal is still open
      if (userDetailModal) {
        handleViewUserDetails(selectedUser);
      }
    } catch (error) {
      toast.error('Failed to update subscription dates');
    }
  };

  const handleResendVerification = async (userId, email) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.post(
        `${API}/admin/users/${userId}/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Verification email sent to ${email}`);
    } catch (error) {
      toast.error('Failed to send verification email');
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject || !emailMessage) {
      toast.error('Please fill in subject and message');
      return;
    }

    const token = localStorage.getItem('session_token');
    setSendingEmail(true);
    
    try {
      await axios.post(
        `${API}/admin/send-email`,
        {
          user_id: emailModal.user.id,
          subject: emailSubject,
          message: emailMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Email sent successfully');
      setEmailModal({ open: false, user: null });
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDeleteUser = (userId) => {
    setDeleteUserDialog({ open: true, userId });
  };

  const confirmDeleteUser = async () => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/admin/users/${deleteUserDialog.userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      setDeleteUserDialog({ open: false, userId: null });
      fetchUsers();
      if (userDetailModal) {
        setUserDetailModal(false);
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleToggleSuspend = (userId, currentStatus) => {
    setSuspendUserDialog({ open: true, userId, currentStatus });
  };

  const confirmToggleSuspend = async () => {
    const token = localStorage.getItem('session_token');
    const newStatus = suspendUserDialog.currentStatus === 'suspended' ? 'active' : 'suspended';
    
    try {
      await axios.put(
        `${API}/admin/users/${suspendUserDialog.userId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${newStatus === 'suspended' ? 'suspended' : 'activated'} successfully`);
      setSuspendUserDialog({ open: false, userId: null, currentStatus: null });
      fetchUsers();
      if (userDetailModal && selectedUser.id === suspendUserDialog.userId) {
        handleViewUserDetails({ ...selectedUser, account_status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      suspended: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: Ban },
      banned: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Shield },
      deleted_by_user: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Trash2 }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status === 'deleted_by_user' ? 'Deleted by User' : (status || 'active')}
      </span>
    );
  };

  const getSubscriptionBadge = (plan) => {
    const planConfig = {
      free: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      premium: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      tipster: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs border ${planConfig[plan]}`}>
        {plan}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading user management...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-gray-400 hover:text-white mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">User Management</h1>
                <p className="text-gray-400">Manage all users, subscriptions, and billing</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-2xl font-bold text-white">{users.length}</div>
                  <div className="text-sm text-gray-400">Total Users</div>
                </div>
              </div>
            </div>
          </div>

        {/* Filters */}
        <Card className="glass-dark border-white/10 mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-300 mb-2">Search</Label>
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300 mb-2">Filter by Plan</Label>
                <Select value={filterPlan} onValueChange={setFilterPlan}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="tipster">Tipster</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300 mb-2">Filter by Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="banned">Banned</SelectItem>
                    <SelectItem value="deleted_by_user">Deleted by User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="glass-dark border-white/10">
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">User</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Plan</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Subscription</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Joined</th>
                    <th className="text-right py-3 px-4 text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-gray-400">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-3 px-4">
                          <div>
                            <div className="text-white font-medium">{u.name}</div>
                            <div className="text-sm text-gray-400">{u.email}</div>
                            {!u.is_verified && (
                              <span className="inline-flex items-center gap-1 mt-1 text-xs text-yellow-400">
                                <XCircle className="w-3 h-3" />
                                Unverified
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(u.account_status)}
                        </td>
                        <td className="py-3 px-4">
                          <Select
                            value={u.subscription_plan}
                            onValueChange={(value) => handleUpdateSubscription(u.id, value)}
                          >
                            <SelectTrigger className="w-32 bg-white/5 border-white/10 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="premium">Premium</SelectItem>
                              <SelectItem value="tipster">Tipster</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {u.subscription_end_date ? (
                              <div>
                                <div className="text-white">
                                  Expires: {formatDate(u.subscription_end_date)}
                                </div>
                                {new Date(u.subscription_end_date) < new Date() && (
                                  <span className="text-red-400 text-xs">Expired</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">No expiry</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-400">
                          {formatDate(u.created_at)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2 justify-end">
                            <Button
                              onClick={() => handleViewUserDetails(u)}
                              size="sm"
                              variant="outline"
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {!u.is_verified && (
                              <Button
                                onClick={() => handleResendVerification(u.id, u.email)}
                                size="sm"
                                variant="outline"
                                className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                                title="Resend verification"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              onClick={() => setEmailModal({ open: true, user: u })}
                              size="sm"
                              variant="outline"
                              className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                              title="Send email"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleToggleSuspend(u.id, u.account_status)}
                              size="sm"
                              variant="outline"
                              className={u.account_status === 'suspended' ? 
                                "border-green-500/50 text-green-400 hover:bg-green-500/20" :
                                "border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                              }
                              title={u.account_status === 'suspended' ? 'Activate' : 'Suspend'}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteUser(u.id)}
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* User Detail Modal */}
      {userDetailModal && selectedUser && (
        <Dialog open={userDetailModal} onOpenChange={setUserDetailModal}>
          <DialogContent className="glass-dark border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white text-2xl">User Details</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 text-sm">Name</Label>
                  <div className="text-white font-medium">{selectedUser.name}</div>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Email</Label>
                  <div className="text-white font-medium">{selectedUser.email}</div>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">User ID</Label>
                  <div className="text-white text-sm font-mono">{selectedUser.id}</div>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Role</Label>
                  <div className="text-white">{selectedUser.role || 'user'}</div>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Account Status</Label>
                  <div>{getStatusBadge(selectedUser.account_status)}</div>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Email Verified</Label>
                  <div>
                    {selectedUser.is_verified ? (
                      <span className="flex items-center text-green-400">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Verified
                      </span>
                    ) : (
                      <span className="flex items-center text-yellow-400">
                        <XCircle className="w-4 h-4 mr-1" />
                        Unverified
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Subscription Info */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Subscription Details
                  </h3>
                  <Button
                    onClick={handleEditSubscriptionDates}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Edit Subscription
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400 text-sm">Current Plan</Label>
                    <div className="mt-1">{getSubscriptionBadge(selectedUser.subscription_plan)}</div>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Status</Label>
                    <div className="text-white">{selectedUser.subscription_status || 'active'}</div>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Start Date</Label>
                    <div className="text-white">{formatDate(selectedUser.subscription_start_date)}</div>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">End Date</Label>
                    <div className="text-white">{formatDate(selectedUser.subscription_end_date)}</div>
                  </div>
                </div>
              </div>

              {/* Activity Stats */}
              {userStats && (
                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2 mb-4">
                    <Activity className="w-5 h-5" />
                    Activity Statistics
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-gray-400 text-sm">Total Bets</div>
                      <div className="text-2xl font-bold text-white">{userStats.total_bets || 0}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-gray-400 text-sm">Tips Created</div>
                      <div className="text-2xl font-bold text-white">{userStats.total_tips || 0}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-gray-400 text-sm">Tips Purchased</div>
                      <div className="text-2xl font-bold text-white">{userStats.tips_purchased || 0}</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label className="text-gray-400 text-sm">Last Login</Label>
                      <div className="text-white">{formatDate(selectedUser.last_login)}</div>
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm">Joined</Label>
                      <div className="text-white">{formatDate(selectedUser.created_at)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Billing History */}
              {billingHistory && billingHistory.length > 0 && (
                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5" />
                    Billing History
                  </h3>
                  
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {billingHistory.map((transaction, index) => (
                      <div key={index} className="bg-white/5 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <div className="text-white font-medium">{transaction.description}</div>
                          <div className="text-sm text-gray-400">{formatDate(transaction.date)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-white font-semibold">${transaction.amount}</div>
                          <div className={`text-xs ${transaction.status === 'completed' ? 'text-green-400' : 'text-yellow-400'}`}>
                            {transaction.status}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Subscription History */}
              {subscriptionHistory && subscriptionHistory.length > 0 && (
                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5" />
                    Subscription History
                  </h3>
                  
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {subscriptionHistory.map((entry, index) => (
                      <div key={index} className="bg-white/5 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              entry.change_type === 'upgrade' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-orange-500/20 text-orange-400'
                            }`}>
                              {entry.change_type === 'upgrade' ? '↑ Upgrade' : '↓ Downgrade'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-400">{formatDate(entry.created_at)}</div>
                        </div>
                        <div className="text-white">
                          <span className="text-gray-400">{entry.old_plan_name}</span>
                          <span className="mx-2 text-gray-500">→</span>
                          <span className="font-medium">{entry.new_plan_name}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {entry.changed_by_type === 'admin' && `Changed by admin: ${entry.changed_by}`}
                          {entry.changed_by_type === 'payment' && `Via payment (${entry.billing_period || 'subscription'})`}
                          {entry.changed_by_type === 'automatic' && 'Automatic (system)'}
                        </div>
                        {entry.reason && (
                          <div className="text-xs text-gray-500 italic mt-1">{entry.reason}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Subscription Modal */}
      {editSubModal && selectedUser && (
        <Dialog open={editSubModal} onOpenChange={setEditSubModal}>
          <DialogContent className="glass-dark border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Subscription</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Subscription End Date</Label>
                <Input
                  type="date"
                  value={subscriptionEndDate}
                  onChange={(e) => setSubscriptionEndDate(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Leave empty for unlimited subscription
                </p>
              </div>
              
              <div>
                <Label className="text-gray-300">Subscription Status</Label>
                <Select value={subscriptionStatus} onValueChange={setSubscriptionStatus}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2 justify-end mt-4">
                <Button
                  onClick={() => setEditSubModal(false)}
                  variant="outline"
                  className="border-white/10 text-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveSubscriptionDates}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Email Modal */}
      {emailModal.open && (
        <Dialog open={emailModal.open} onOpenChange={(open) => setEmailModal({ open, user: null })}>
          <DialogContent className="glass-dark border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Send Email to {emailModal.user?.email}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Subject</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Message</Label>
                <Textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Email message..."
                  rows={8}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button
                  onClick={() => setEmailModal({ open: false, user: null })}
                  variant="outline"
                  className="border-white/10 text-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={deleteUserDialog.open}
        onOpenChange={(open) => setDeleteUserDialog({ open, userId: null })}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone and will remove all associated data."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={confirmDeleteUser}
      />

      <ConfirmDialog
        open={suspendUserDialog.open}
        onOpenChange={(open) => setSuspendUserDialog({ open: false, userId: null, currentStatus: null })}
        title={suspendUserDialog.currentStatus === 'suspended' ? 'Activate User' : 'Suspend User'}
        description={
          suspendUserDialog.currentStatus === 'suspended' 
            ? 'Activate this user account? They will regain full access to the platform.'
            : 'Suspend this user account? They will lose access to the platform.'
        }
        confirmText={suspendUserDialog.currentStatus === 'suspended' ? 'Activate' : 'Suspend'}
        cancelText="Cancel"
        variant={suspendUserDialog.currentStatus === 'suspended' ? 'default' : 'destructive'}
        onConfirm={confirmToggleSuspend}
      />
    </Layout>
  );
};

export default AdminCRM;
